/*
SDK wrapper for user-data-platform APIs.
See https://github.com/NerdWallet/user-data-platform for details.
*/
import dataApiFactory from './data-api-factory';
export default function UserDataPlatformSDK(ServiceSDK, config) {
  var userDataPlatformBaseUrl = config.userDataPlatformBaseUrl;

  if (!userDataPlatformBaseUrl) {
    throw new Error('UserDataPlatformSDK requires a valid userDataPlatformBaseUrl');
  }

  function getUserDataPlatformRoute(namespace, version) {
    return function (endpoint) {
      return ServiceSDK.nwApiRoute(userDataPlatformBaseUrl, namespace, version, endpoint);
    };
  }

  function buildDataApiFactory(namespace, version) {
    return dataApiFactory(ServiceSDK, getUserDataPlatformRoute(namespace, version));
  }

  var member = {
    /*
      Profiles API SDK factory for member data.
      - profileName: IdentitySDK.PROFILES.<PROFILE_NAME> constant
       Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        sdk.member.profileApi('home_consumer_profile').postMy({'first_name': 'Foo'}).then(...)
        sdk.member.profileApi('home_consumer_profile').getMy().then(...)
      */
    profileApi: function profileApi(profileName) {
      var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      return buildDataApiFactory('member-data', version).getSingularIdentityEntityAPI(profileName);
    },

    /*
      Get batch profiles API for member data.
      - includeProfiles: [profile_name, ...]
      @deprecated - Use getProfilesV2 instead
       Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        sdk.member.getProfiles(['financial_profile'], serverRequest).then(...);
      */
    getProfiles: function getProfiles() {
      var includeProfiles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var serverRequest = arguments.length > 1 ? arguments[1] : undefined;
      var apiFactory = buildDataApiFactory('member-data', 1).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.getMy(params, serverRequest);
    },

    /*
      Get batch profiles API for member data, optionally specifying API versions for each profile.
      - includeProfiles: {profile_name: api_version}
       If api_version is null, the default behavior is to use the highest api version. New clients should
      explicitly choose an api version to avoid unexpected version changes.
       Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        sdk.member.getProfilesV2({'financial_profile': 1}, serverRequest).then(...);
      */
    getProfilesV2: function getProfilesV2() {
      var includeProfiles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var serverRequest = arguments.length > 1 ? arguments[1] : undefined;
      var apiFactory = buildDataApiFactory('member-data', 2).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.getMy(params, serverRequest);
    },

    /*
      Get batch profiles API for member data, optionally specifying API versions for each profile.
      - identityID: UUID that uniquely identifies the member account
      - includeProfiles: {profile_name: api_version}
      If api_version is null, the default behavior is to use the highest api version. New clients should
      explicitly choose an api version to avoid unexpected version changes.
      Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        const profiles = await sdk.member.getAnyProfilesV2(identityID, {'financial_profile': 1}, serverRequest);
      */
    getAnyProfilesV2: function getAnyProfilesV2(identityID) {
      var includeProfiles = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var serverRequest = arguments.length > 2 ? arguments[2] : undefined;
      var apiFactory = buildDataApiFactory('member-data', 2).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.get(identityID, params, serverRequest);
    },
    productShoppingActions: {
      getAllMy: buildDataApiFactory('member-data', 1).getPluralIdentityEntityAPI('product_shopping_actions').getAllMy,
      // WARNING: POST is disabled on prod - this API is used to construct test data only
      postMy: buildDataApiFactory('member-data', 1).getPluralIdentityEntityAPI('product_shopping_actions').postMy
    },
    productShoppingActionsV2: {
      getAllMy: buildDataApiFactory('member-data', 2).getPluralIdentityEntityAPI('product_shopping_actions').getAllMy,
      // WARNING: POST is disabled on prod - this API is used to construct test data only
      postMy: buildDataApiFactory('member-data', 2).getPluralIdentityEntityAPI('product_shopping_actions').postMy
    }
  };
  var visitor = {
    /*
      Profiles API SDK factory for visitor data.
      - profileName: IdentitySDK.PROFILES.<PROFILE_NAME> constant
       Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        sdk.member.profileApi('global_profile').postMy({'first_name': 'Foo'}).then(...)
        sdk.member.profileApi('global_profile').getMy().then(...)
      */
    profileApi: function profileApi(profileName) {
      var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      return buildDataApiFactory('visitor-data', version).getSingularIdentityEntityAPI(profileName);
    },

    /*
      Get batch profiles API for visitor data.
      - includeProfiles: [profile_name, ...]
      @deprecated - Use getProfilesV2 instead
       Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        sdk.visitor.getProfiles(['financial_profile'], serverRequest).then(...);
      */
    getProfiles: function getProfiles() {
      var includeProfiles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var serverRequest = arguments.length > 1 ? arguments[1] : undefined;
      var apiFactory = buildDataApiFactory('visitor-data', 1).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.getMy(params, serverRequest);
    },

    /*
      Get batch profiles API for visitor data, optionally specifying API versions for each profile.
      - includeProfiles: {profile_name: api_version}
       If api_version is null, the default behavior is to use the highest api version. New clients should
      explicitly choose an api version to avoid unexpected version changes.
       Example usage:
        sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
        sdk.visitor.getProfilesV2({'financial_profile': 1}, serverRequest).then(...);
      */
    getProfilesV2: function getProfilesV2() {
      var includeProfiles = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var serverRequest = arguments.length > 1 ? arguments[1] : undefined;
      var apiFactory = buildDataApiFactory('visitor-data', 2).getSingularIdentityEntityAPI('profiles');
      var params = {
        include_profiles: JSON.stringify(includeProfiles)
      };
      return apiFactory.getMy(params, serverRequest);
    },
    productShoppingActions: {
      getAllMy: buildDataApiFactory('visitor-data', 1).getPluralIdentityEntityAPI('product_shopping_actions').getAllMy,
      // WARNING: POST is disabled on prod - this API is used to construct test data only
      postMy: buildDataApiFactory('visitor-data', 1).getPluralIdentityEntityAPI('product_shopping_actions').postMy
    },
    productShoppingActionsV2: {
      getAllMy: buildDataApiFactory('visitor-data', 2).getPluralIdentityEntityAPI('product_shopping_actions').getAllMy,
      // WARNING: POST is disabled on prod - this API is used to construct test data only
      postMy: buildDataApiFactory('visitor-data', 2).getPluralIdentityEntityAPI('product_shopping_actions').postMy
    }
  };
  return {
    member: member,
    visitor: visitor,

    /*
    ** ALL METHODS BELOW ARE DEPRECATED **
    Please use member and visitor namespaced profiles API's above
     Profiles API SDK factory.
    - profileName: IdentitySDK.PROFILES.<PROFILE_NAME> constant
     Example usage:
      sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
      sdk.profileApi('global_profile').postMy({'first_name': 'Foo'}).then(...)
      sdk.profileApi('global_profile').getMy().then(...)
    */
    profileApi: function profileApi(profileName) {
      var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      return visitor.profileApi(profileName, version);
    },

    /*
    Legacy bespoke Profiles API SDKs.
     Example usage:
      sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
      sdk.globalProfile.postMy({'first_name': 'Foo'}).then(...)
    */
    financialProfile: visitor.profileApi('financial_profile', 1),
    globalProfile: visitor.profileApi('global_profile', 1),
    homeConsumerProfile: visitor.profileApi('home_consumer_profile', 1),
    wpSelfReportedDataProfile: visitor.profileApi('wp_self_reported_data_profile', 1),
    // Plural Identity Entity
    userReportedFinancialAccounts: buildDataApiFactory('visitor-data', 1).getPluralIdentityEntityAPI('user_reported_financial_accounts'),

    /*
    Product shopping actions API SDK.
    See https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/414154910/Visitor+Product+Shopping+Actions+Integration+Guide
    for details
     Example usage:
      sdk = UserDataPlatformSDK(serviceSDK, {userDataPlatformBaseUrl: ...});
      sdk.productShoppingActions.getAllMy({vertical: 'Credit Cards'}).then(...)
    */
    productShoppingActions: {
      getAllMy: visitor.productShoppingActions.getAllMy,
      // WARNING: POST is disabled on prod - this API is used to construct test data only
      postMy: visitor.productShoppingActions.postMy
    },
    productShoppingActionsV2: {
      getAllMy: visitor.productShoppingActionsV2.getAllMy,
      // WARNING: POST is disabled on prod - this API is used to construct test data only
      postMy: visitor.productShoppingActionsV2.postMy
    }
  };
}